import React, { useState } from 'react';
import { Skeleton } from 'src/components/ui/skeleton';
import { RawDataPoint } from 'src/redux/admin/reporting/reporting.types';

interface LineItemMetrics {
  id: number | null;
  name: string;
  impressions: number;
  clicks: number;
  views: number;
  spend: number;
  conversions: number;
  video_starts: number;
  video_completes: number;
  audio_starts: number;
  audio_completes: number;
  ctr: number;
  cpa: number;
  cpm: number;
  video_completion_rate: number;
  audio_completion_rate: number;
}

interface CampaignMetrics {
  id: number;
  name: string;
  impressions: number;
  clicks: number;
  views: number;
  spend: number;
  conversions: number;
  video_starts: number;
  video_completes: number;
  audio_starts: number;
  audio_completes: number;
  ctr: number;
  cpa: number;
  cpm: number;
  video_completion_rate: number;
  audio_completion_rate: number;
  lineItems?: LineItemMetrics[];
}

interface CampaignBreakdownTableProps {
  data: RawDataPoint[];
  isLoading: boolean;
  shouldShowMetric: (metric: string) => boolean;
}

const formatNumber = (num: number, decimals: boolean = false): string => {
  if (!isFinite(num) || isNaN(num)) return 'N/A';
  if (decimals) {
    return num.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  return num.toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatCurrency = (value: number): string => {
  if (!isFinite(value) || isNaN(value)) return 'N/A';
  return `$${formatNumber(value, true)}`;
};

const formatPercentage = (value: number): string => {
  if (!isFinite(value) || isNaN(value)) return 'N/A';
  return `${formatNumber(value * 100, true)}%`;
};

const CampaignBreakdownTable: React.FC<CampaignBreakdownTableProps> = ({
  data,
  isLoading,
  shouldShowMetric,
}) => {
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<number>>(new Set());

  // Process and aggregate data by campaign and line item
  const campaignMetrics: CampaignMetrics[] = Object.values(
    data.reduce((campaigns: { [key: number]: Omit<CampaignMetrics, 'lineItems'> & { lineItems: Record<string, LineItemMetrics> } }, row) => {
      // Initialize or update campaign
      if (!campaigns[row.campaign_id]) {
        campaigns[row.campaign_id] = {
          id: row.campaign_id,
          name: row.campaign_name,
          impressions: 0,
          clicks: 0,
          views: 0,
          spend: 0,
          conversions: 0,
          video_starts: 0,
          video_completes: 0,
          audio_starts: 0,
          audio_completes: 0,
          ctr: 0,
          cpa: 0,
          cpm: 0,
          video_completion_rate: 0,
          audio_completion_rate: 0,
          lineItems: {},
        };
      }

      const campaign = campaigns[row.campaign_id];
      campaign.impressions += row.impressions;
      campaign.clicks += row.clicks;
      campaign.views += row.views;
      campaign.spend += row.spend;
      campaign.conversions += row.conversions;
      campaign.video_starts += row.video_starts;
      campaign.video_completes += row.video_completes;
      campaign.audio_starts += row.audio_starts;
      campaign.audio_completes += row.audio_completes;
      campaign.ctr = campaign.clicks / campaign.impressions;
      campaign.cpa = campaign.spend / campaign.conversions;
      campaign.cpm = (campaign.spend / campaign.impressions) * 1000;
      campaign.video_completion_rate = campaign.video_completes / campaign.video_starts;
      campaign.audio_completion_rate = campaign.audio_completes / campaign.audio_starts;

      // Update line item if it exists
      if (row.line_item_id && row.line_item_name) {
        if (!campaign.lineItems[row.line_item_id]) {
          campaign.lineItems[row.line_item_id] = {
            id: row.line_item_id,
            name: row.line_item_name ?? '',
            impressions: 0,
            clicks: 0,
            views: 0,
            spend: 0,
            conversions: 0,
            video_starts: 0,
            video_completes: 0,
            audio_starts: 0,
            audio_completes: 0,
            ctr: 0,
            cpa: 0,
            cpm: 0,
            video_completion_rate: 0,
            audio_completion_rate: 0,
          };
        }

        const lineItem = campaign.lineItems[row.line_item_id];
        lineItem.impressions += row.impressions;
        lineItem.clicks += row.clicks;
        lineItem.views += row.views;
        lineItem.spend += row.spend;
        lineItem.conversions += row.conversions;
        lineItem.video_starts += row.video_starts;
        lineItem.video_completes += row.video_completes;
        lineItem.audio_starts += row.audio_starts;
        lineItem.audio_completes += row.audio_completes;
        lineItem.ctr = lineItem.clicks / lineItem.impressions;
        lineItem.cpa = lineItem.spend / lineItem.conversions;
        lineItem.cpm = (lineItem.spend / lineItem.impressions) * 1000;
        lineItem.video_completion_rate = lineItem.video_completes / lineItem.video_starts;
        lineItem.audio_completion_rate = lineItem.audio_completes / lineItem.audio_starts;
      }

      return campaigns;
    }, {})
  ).map(campaign => ({
    ...campaign,
    lineItems: Object.values(campaign.lineItems).length > 0 ? Object.values(campaign.lineItems) : undefined,
  }));

  const toggleCampaign = (campaignId: number) => {
    setExpandedCampaigns(prev => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  };

  const interactionTitle = () => {
    let clicks = 0;
    let views = 0;

    campaignMetrics.forEach(campaign => {
      clicks += campaign.clicks;
      views += campaign.views;
    });

    if (clicks > 0 && views > 0) {
      return 'Interactions';
    } else if (clicks > 0 && views === 0) {
      return 'Clicks';
    } else {
      return 'Views';
    }
  };

  const interactionValue = (campaign: CampaignMetrics) => {
    if (campaign.clicks > 0 && campaign.views > 0) {
      return campaign.clicks + campaign.views;
    } else if (campaign.clicks > 0 && campaign.views === 0) {
      return campaign.clicks;
    } else {
      return campaign.views;
    }
  };

  const interactionValueItem = (item: LineItemMetrics) => {
    if (item.clicks > 0 && item.views > 0) {
      return item.clicks + item.views;
    } else if (item.clicks > 0 && item.views === 0) {
      return item.clicks;
    } else {
      return item.views;
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-gray-400 p-8 flex-1">
      <div className="text-2xl font-light mb-4">
        Campaign Performance Breakdown
      </div>
      <div className="overflow-x-auto">
        {isLoading ? (
          <Skeleton className="w-full h-[200px]" />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-400">
                <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">
                  Campaign / Line Item
                </th>
                {shouldShowMetric('clicks') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    {interactionTitle()}
                  </th>
                )}
                {shouldShowMetric('impressions') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    Impressions
                  </th>
                )}
                {shouldShowMetric('conversions') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    Conv.
                  </th>
                )}
                {shouldShowMetric('ctr') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    CTR
                  </th>
                )}
                {shouldShowMetric('spend') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    Cost
                  </th>
                )}
                {shouldShowMetric('cpm') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    CPM
                  </th>
                )}
                {shouldShowMetric('cpa') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    CPA
                  </th>
                )}
                {shouldShowMetric('video_completion_rate') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    Video CR
                  </th>
                )}
                {shouldShowMetric('audio_completion_rate') && (
                  <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">
                    Audio CR
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {campaignMetrics.map(campaign => (
                <React.Fragment key={campaign.id}>
                  <tr
                    className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleCampaign(campaign.id)}
                  >
                    <td className="py-2 px-4 font-medium flex items-center">
                      <span className="mr-2 text-gray-500 text-sm">
                        {expandedCampaigns.has(campaign.id) ? '▼' : '►'}
                      </span>
                      {campaign.name}
                    </td>
                    {shouldShowMetric('clicks') && (
                      <td className="text-right py-2 px-4">
                        {formatNumber(interactionValue(campaign))}
                      </td>
                    )}
                    {shouldShowMetric('impressions') && (
                      <td className="text-right py-2 px-4">
                        {formatNumber(campaign.impressions)}
                      </td>
                    )}
                    {shouldShowMetric('conversions') && (
                      <td className="text-right py-2 px-4">
                        {formatNumber(campaign.conversions)}
                      </td>
                    )}
                    {shouldShowMetric('ctr') && (
                      <td className="text-right py-2 px-4">
                        {formatPercentage(campaign.ctr)}
                      </td>
                    )}
                    {shouldShowMetric('spend') && (
                      <td className="text-right py-2 px-4">
                        {formatCurrency(campaign.spend)}
                      </td>
                    )}
                    {shouldShowMetric('cpm') && (
                      <td className="text-right py-2 px-4">
                        {formatCurrency(campaign.cpm)}
                      </td>
                    )}
                    {shouldShowMetric('cpa') && (
                      <td className="text-right py-2 px-4">
                        {formatCurrency(campaign.cpa)}
                      </td>
                    )}
                    {shouldShowMetric('video_completion_rate') && (
                      <td className="text-right py-2 px-4">
                        {formatPercentage(campaign.video_completion_rate)}
                      </td>
                    )}
                    {shouldShowMetric('audio_completion_rate') && (
                      <td className="text-right py-2 px-4">
                        {formatPercentage(campaign.audio_completion_rate)}
                      </td>
                    )}
                  </tr>
                  {expandedCampaigns.has(campaign.id) && campaign.lineItems?.map(item => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 bg-gray-50"
                    >
                      <td className="py-2 px-4 pl-10">
                        {item.name}
                      </td>
                      {shouldShowMetric('clicks') && (
                        <td className="text-right py-2 px-4">
                          {formatNumber(interactionValueItem(item))}
                        </td>
                      )}
                      {shouldShowMetric('impressions') && (
                        <td className="text-right py-2 px-4">
                          {formatNumber(item.impressions)}
                        </td>
                      )}
                      {shouldShowMetric('conversions') && (
                        <td className="text-right py-2 px-4">
                          {formatNumber(item.conversions)}
                        </td>
                      )}
                      {shouldShowMetric('ctr') && (
                        <td className="text-right py-2 px-4">
                          {formatPercentage(item.ctr)}
                        </td>
                      )}
                      {shouldShowMetric('spend') && (
                        <td className="text-right py-2 px-4">
                          {formatCurrency(item.spend)}
                        </td>
                      )}
                      {shouldShowMetric('cpm') && (
                        <td className="text-right py-2 px-4">
                          {formatCurrency(item.cpm)}
                        </td>
                      )}
                      {shouldShowMetric('cpa') && (
                        <td className="text-right py-2 px-4">
                          {formatCurrency(item.cpa)}
                        </td>
                      )}
                      {shouldShowMetric('video_completion_rate') && (
                        <td className="text-right py-2 px-4">
                          {formatPercentage(item.video_completion_rate)}
                        </td>
                      )}
                      {shouldShowMetric('audio_completion_rate') && (
                        <td className="text-right py-2 px-4">
                          {formatPercentage(item.audio_completion_rate)}
                        </td>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CampaignBreakdownTable;